import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist';
import { AnnotationMode } from 'pdfjs-dist';
import { getMyStage3DocumentComments } from '../../api/content-api';
import CloseIcon from '@mui/icons-material/Close';
import { normalizeViewerRotationDeg } from '../../utils/stage3ViewerNotes';

export type StudentWorkViewerAdminPreviewMeta = {
	work_code?: string;
	student_name?: string;
	third_a_stage_points?: string | number | null;
	third_a_juror1_points?: string | number | null;
	third_a_juror2_points?: string | number | null;
	shared_review?: boolean;
	assigned_juror1_name?: string;
	assigned_juror2_name?: string;
	assigned_jury_pair_name?: string;
	document_type?: string;
};

type Props = {
	open: boolean;
	onClose: () => void;
	documentId: number | null;
	url: string;
	title?: string;
	watermark: string;
	token: string | null;
	commentsFetcher?: (documentId: number) => Promise<any>;
	onAccessError?: (message: string) => void;
	/** Dodatkowy obrót widoku (0/90/180/270) względem /Rotate strony PDF — ten sam co render + komentarze. */
	pageRotations?: Record<number, number>;
	/** Przesunięcie podświetleń w pionie (px, + = w dół), per strona. */
	commentOffsetYByPage?: Record<number, number>;
	/** Domyślne przesunięcie pionowe (px), gdy brak wpisu dla strony. */
	commentOffsetYGlobal?: number | null;
	/** Osobny obrót tylko warstwy komentarzy (0/90/180/270), px-geometry — skan bez zmian. */
	commentRotationDegByPage?: Record<number, number>;
	commentOffsetXByPage?: Record<number, number>;
	commentOffsetXGlobal?: number | null;
	/** Skala prostokątów od środka strony (100 = 100%), np. 70 i 150. */
	commentScaleXPercent?: number;
	commentScaleYPercent?: number;
	/** Ukryj żółte nakładki HTML (komentarze tekstowe w panelu zostają). */
	hideOverlayHighlightBoxes?: boolean;
	/** Nie renderuj adnotacji PDF na obrazie strony (usuwa wbudowane podświetlenia z PDF). */
	hidePdfAnnotationRender?: boolean;
	/** Opcjonalny blok weryfikacji (np. admin + kompletność). */
	adminPreviewMeta?: StudentWorkViewerAdminPreviewMeta | null;
	compatStreamUrlBuilder?: (documentId: number) => string;
	showRotationInfo?: boolean;
};

type OverlayBox = {
	key: string;
	no: number;
	left: number;
	top: number;
	width: number;
	height: number;
};

type CommentItem = {
	no: number;
	page: number | null;
	quoted: string;
	content: string;
	type: string;
	rect: number[];
	quadpoints: number[];
	/** Współrzędne już w przestrzeni viewport PDF.js (jak render strony) — dla /Rotate 90/270 itd. */
	cachedBoxes?: OverlayBox[];
};

type RenderedPage = {
	pageNo: number;
	width: number;
	height: number;
	imageSrc: string;
	pdfRotation: number;
};

const isPointLikeCommentType = (type: string): boolean => {
	const t = String(type || '').toLowerCase();
	return t === 'text' || t === 'popup';
};

const drawWatermark = (canvas: HTMLCanvasElement, label: string, extraRotationDeg = 0) => {
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return;
	}
	const chunks = String(label || '')
		.split('|')
		.filter((part) => part.replace(/\s/g, '').length > 0);
	const rows = chunks.length > 0 ? chunks : [String(label || '').trim() || 'OLiJP - podgląd'];
	const stepX = 340;
	const stepY = 210;
	const rowGap = 22;
	const baseShiftY = -((rows.length - 1) * rowGap) / 2;
	ctx.save();
	ctx.globalAlpha = 0.16;
	ctx.fillStyle = '#b71c1c';
	ctx.font = 'bold 16px Arial';
	ctx.translate(canvas.width / 2, canvas.height / 2);
	ctx.rotate(((-25 + extraRotationDeg) * Math.PI) / 180);
	for (let y = -canvas.height; y < canvas.height; y += stepY) {
		for (let x = -canvas.width; x < canvas.width; x += stepX) {
			rows.forEach((rowText, idx) => {
				ctx.fillText(rowText, x, y + baseShiftY + idx * rowGap);
			});
		}
	}
	ctx.restore();
};

const PDF_VIEW_SCALE = 1.35;

const totalViewportRotation = (pdfPageRotate: number, userAdjust: unknown): number => {
	const base = Number(pdfPageRotate) || 0;
	const add = normalizeViewerRotationDeg(userAdjust);
	return (base + add + 360) % 360;
};

/** Obrót prostokątów podświetleń wokół środka strony (px), po przeliczeniu z %. */
const rotateOverlayBoxesAroundPageCenter = (
	boxes: OverlayBox[],
	deg: number,
	pageW: number,
	pageH: number,
): OverlayBox[] => {
	const norm = ((Math.round(deg) % 360) + 360) % 360;
	if (!norm) {
		return boxes;
	}
	const rad = (norm * Math.PI) / 180;
	const cos = Math.cos(rad);
	const sin = Math.sin(rad);
	const cx = pageW / 2;
	const cy = pageH / 2;
	return boxes.map((b) => {
		const leftPx = (b.left / 100) * pageW;
		const topPx = (b.top / 100) * pageH;
		const wPx = (b.width / 100) * pageW;
		const hPx = (b.height / 100) * pageH;
		const corners = [
			[leftPx, topPx],
			[leftPx + wPx, topPx],
			[leftPx + wPx, topPx + hPx],
			[leftPx, topPx + hPx],
		];
		const rotated = corners.map(([x, y]) => {
			const dx = x - cx;
			const dy = y - cy;
			return [dx * cos - dy * sin + cx, dx * sin + dy * cos + cy];
		});
		const xs = rotated.map((c) => c[0]);
		const ys = rotated.map((c) => c[1]);
		const nl = Math.min(...xs);
		const nt = Math.min(...ys);
		const nw = Math.max(...xs) - nl;
		const nh = Math.max(...ys) - nt;
		return {
			...b,
			left: (nl / pageW) * 100,
			top: (nt / pageH) * 100,
			width: (nw / pageW) * 100,
			height: (nh / pageH) * 100,
		};
	});
};

const scaleOverlayBoxesFromCenter = (
	boxes: OverlayBox[],
	scaleXPercent: number,
	scaleYPercent: number,
	pageW: number,
	pageH: number,
): OverlayBox[] => {
	const sx = (Number(scaleXPercent) || 100) / 100;
	const sy = (Number(scaleYPercent) || 100) / 100;
	if (Math.abs(sx - 1) < 0.0001 && Math.abs(sy - 1) < 0.0001) {
		return boxes;
	}
	return boxes.map((b) => {
		const leftPx = (b.left / 100) * pageW;
		const topPx = (b.top / 100) * pageH;
		const wPx = (b.width / 100) * pageW;
		const hPx = (b.height / 100) * pageH;
		const cx = leftPx + wPx / 2;
		const cy = topPx + hPx / 2;
		const ncx = pageW / 2 + (cx - pageW / 2) * sx;
		const ncy = pageH / 2 + (cy - pageH / 2) * sy;
		const nwPx = wPx * sx;
		const nhPx = hPx * sy;
		return {
			...b,
			left: ((ncx - nwPx / 2) / pageW) * 100,
			top: ((ncy - nhPx / 2) / pageH) * 100,
			width: (nwPx / pageW) * 100,
			height: (nhPx / pageH) * 100,
		};
	});
};

const getPdfAnnotationModeForRender = (hidePdfAnnotations: boolean): number =>
	hidePdfAnnotations ? AnnotationMode.DISABLE : AnnotationMode.ENABLE;

/**
 * Mapuje Rect / QuadPoints z PDF na procenty względem tego samego viewportu co `page.render`
 * (uwzględnia /Rotate strony — inaczej niż ręczny obrót tylko dla 180°).
 */
const buildAnnotationOverlayBoxes = (viewport: any, item: CommentItem): OverlayBox[] => {
	const vw = viewport.width as number;
	const vh = viewport.height as number;
	const boxes: OverlayBox[] = [];
	const pointLike = isPointLikeCommentType(item.type);

	const pushPercent = (minX: number, maxX: number, minY: number, maxY: number, key: string) => {
		const left = (Math.min(minX, maxX) / vw) * 100;
		const top = (Math.min(minY, maxY) / vh) * 100;
		const width = (Math.abs(maxX - minX) / vw) * 100;
		const height = (Math.abs(maxY - minY) / vh) * 100;
		boxes.push({ key, no: item.no, left, top, width, height });
	};

	if (!pointLike && item.quadpoints.length >= 8) {
		for (let i = 0; i + 7 < item.quadpoints.length; i += 8) {
			const quad = item.quadpoints.slice(i, i + 8);
			const xs: number[] = [];
			const ys: number[] = [];
			for (let j = 0; j < 8; j += 2) {
				const p = viewport.convertToViewportPoint(quad[j], quad[j + 1]);
				xs.push(p[0]);
				ys.push(p[1]);
			}
			pushPercent(Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys), `${item.no}-q-${i}`);
		}
	} else if (item.rect.length >= 4) {
		const xMin = Math.min(item.rect[0], item.rect[2]);
		const xMax = Math.max(item.rect[0], item.rect[2]);
		const yMin = Math.min(item.rect[1], item.rect[3]);
		const yMax = Math.max(item.rect[1], item.rect[3]);
		if (pointLike) {
			const centerX = (xMin + xMax) / 2;
			const centerY = (yMin + yMax) / 2;
			const halfW = 7;
			const halfH = 7;
			const vr = viewport.convertToViewportRectangle([
				centerX - halfW,
				centerY - halfH,
				centerX + halfW,
				centerY + halfH,
			]);
			pushPercent(vr[0], vr[2], vr[1], vr[3], `${item.no}-m`);
		} else {
			const vr = viewport.convertToViewportRectangle([item.rect[0], item.rect[1], item.rect[2], item.rect[3]]);
			pushPercent(vr[0], vr[2], vr[1], vr[3], `${item.no}-r`);
		}
	}
	return boxes;
};

const enrichCommentsWithPdfViewport = async (
	pdf: any,
	items: CommentItem[],
	pageRotationAdjust: Record<number, number>,
): Promise<CommentItem[]> => {
	const pageCache = new Map<number, any>();
	const getPageCached = async (num: number) => {
		if (!pageCache.has(num)) {
			pageCache.set(num, await pdf.getPage(num));
		}
		return pageCache.get(num);
	};

	const out: CommentItem[] = [];
	for (const item of items) {
		const stripped: CommentItem = { ...item, cachedBoxes: undefined };
		if (!item.page || item.page < 1) {
			out.push(stripped);
			continue;
		}
		if (item.rect.length < 4 && item.quadpoints.length < 8) {
			out.push(stripped);
			continue;
		}
		try {
			const page = await getPageCached(item.page);
			const rotation = totalViewportRotation(page.rotate || 0, pageRotationAdjust[item.page]);
			const viewport = page.getViewport({ scale: PDF_VIEW_SCALE, rotation });
			const boxes = buildAnnotationOverlayBoxes(viewport, item);
			out.push(boxes.length > 0 ? { ...stripped, cachedBoxes: boxes } : stripped);
		} catch {
			out.push(stripped);
		}
	}
	return out;
};

const isLikelyBlankCanvas = (canvas: HTMLCanvasElement): boolean => {
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		return false;
	}
	const width = canvas.width;
	const height = canvas.height;
	if (width <= 0 || height <= 0) {
		return false;
	}
	const step = 24;
	let samples = 0;
	let nearWhite = 0;
	for (let y = 0; y < height; y += step) {
		for (let x = 0; x < width; x += step) {
			const px = ctx.getImageData(x, y, 1, 1).data;
			samples += 1;
			if (px[0] > 240 && px[1] > 240 && px[2] > 240) {
				nearWhite += 1;
			}
		}
	}
	if (samples === 0) {
		return false;
	}
	return nearWhite / samples > 0.98;
};

const StudentWorkViewer = ({
	open,
	onClose,
	documentId,
	url,
	title,
	watermark,
	token,
	commentsFetcher,
	onAccessError,
	pageRotations,
	commentOffsetYByPage,
	commentOffsetYGlobal,
	commentOffsetXByPage,
	commentOffsetXGlobal,
	commentScaleXPercent,
	commentScaleYPercent,
	hideOverlayHighlightBoxes,
	hidePdfAnnotationRender,
	commentRotationDegByPage,
	adminPreviewMeta,
	compatStreamUrlBuilder,
	showRotationInfo = false,
}: Props) => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [pages, setPages] = useState<RenderedPage[]>([]);
	const [comments, setComments] = useState<CommentItem[]>([]);
	const [commentsMeta, setCommentsMeta] = useState<any | null>(null);
	const [activeNo, setActiveNo] = useState<number | null>(null);
	const [compatMode, setCompatMode] = useState(false);
	const [compatFallbackAvailable, setCompatFallbackAvailable] = useState(false);
	const [compatBlobUrl, setCompatBlobUrl] = useState('');
	const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
	const markerRefs = useRef<Record<number, HTMLDivElement | null>>({});
	/** Surowe komentarze (rect/quad) do ponownego mapowania po przekodowaniu PDF (compat). */
	const rawCommentsRef = useRef<CommentItem[]>([]);

	const safeWatermark = useMemo(() => watermark || 'OLiJP - podgląd', [watermark]);
	const withPosition = useMemo(
		() => comments.filter((item) => item.page !== null && (item.rect.length >= 4 || item.quadpoints.length >= 8)),
		[comments],
	);
	const withoutPosition = useMemo(
		() => comments.filter((item) => !withPosition.some((x) => x.no === item.no)),
		[comments, withPosition],
	);
	const hasAnyComments = comments.length > 0;

	useEffect(() => {
		const ver = (pdfjsLib as any).version;
		(pdfjsLib as any).GlobalWorkerOptions.workerSrc =
			`https://cdn.jsdelivr.net/npm/pdfjs-dist@${ver}/build/pdf.worker.min.mjs`;
	}, []);

	useEffect(() => {
		if (!open) {
			return;
		}
		setCompatMode(false);
		setCompatFallbackAvailable(false);
		if (compatBlobUrl) {
			URL.revokeObjectURL(compatBlobUrl);
			setCompatBlobUrl('');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, documentId, url]);

	useEffect(() => {
		if (!compatMode || !documentId) {
			if (compatBlobUrl) {
				URL.revokeObjectURL(compatBlobUrl);
				setCompatBlobUrl('');
			}
			return;
		}
		let cancelled = false;
		setLoading(true);
		setError('');
		const reEncode = async () => {
			try {
				const compatUrl = compatStreamUrlBuilder
					? compatStreamUrlBuilder(documentId)
					: `/wp-json/olympics/stage3-documents/${documentId}/stream-compat`;
				const resp = await fetch(compatUrl, {
					credentials: 'include',
					headers: token ? { Authorization: `Bearer ${token}` } : {},
				});
				if (!resp.ok) {
					const errJson = await resp.json().catch(() => null);
					throw new Error(errJson?.message || `HTTP ${resp.status}`);
				}
				const blob = await resp.blob();
				if (cancelled) return;
				const blobUrl = URL.createObjectURL(blob);
				setCompatBlobUrl(blobUrl);

				const ver = (pdfjsLib as any).version;
				const loadingTask = (pdfjsLib as any).getDocument({
					url: blobUrl,
					cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${ver}/cmaps/`,
					cMapPacked: true,
					standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${ver}/standard_fonts/`,
					isEvalSupported: true,
				});
				const pdf = await loadingTask.promise;
				const renderedPages: RenderedPage[] = [];
				const rotMap = pageRotations || {};
				for (let i = 1; i <= pdf.numPages; i++) {
					const page = await pdf.getPage(i);
					const rotation = totalViewportRotation(page.rotate || 0, rotMap[i]);
					const viewport = page.getViewport({ scale: PDF_VIEW_SCALE, rotation });
					const canvas = document.createElement('canvas');
					canvas.width = viewport.width;
					canvas.height = viewport.height;
					const context = canvas.getContext('2d');
					if (!context) continue;
					await page.render({
						canvasContext: context,
						viewport,
						annotationMode: getPdfAnnotationModeForRender(!!hidePdfAnnotationRender),
					}).promise;
					drawWatermark(canvas, safeWatermark, 0);
					renderedPages.push({
						pageNo: i,
						width: viewport.width,
						height: viewport.height,
						imageSrc: canvas.toDataURL('image/png'),
						pdfRotation: page.rotate || 0,
					});
				}
				if (!cancelled) {
					setPages(renderedPages);
					setCompatFallbackAvailable(false);
					const enriched = await enrichCommentsWithPdfViewport(pdf, rawCommentsRef.current, rotMap);
					if (!cancelled) {
						setComments(enriched);
					}
				}
			} catch (e: any) {
				if (!cancelled) {
					setError(String(e?.message || 'Nie udało się przekodować PDF.'));
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		reEncode();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [compatMode, documentId, token, pageRotations, hidePdfAnnotationRender]);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			if (!open || !url) {
				setPages([]);
				setComments([]);
				setCommentsMeta(null);
				markerRefs.current = {};
				setCompatMode(false);
				setCompatFallbackAvailable(false);
				if (compatBlobUrl) {
					URL.revokeObjectURL(compatBlobUrl);
					setCompatBlobUrl('');
				}
				return;
			}
			setLoading(true);
			setError('');
			let rawItems: any[] = [];
			let hasUnsupportedCompression = false;
			try {
				if (documentId) {
					try {
						const commentsResponse = commentsFetcher
							? await commentsFetcher(documentId)
							: await getMyStage3DocumentComments(token, documentId);
						rawItems = Array.isArray(commentsResponse?.data?.items) ? commentsResponse.data.items : [];
						const meta = commentsResponse?.data?.meta || null;
						setCommentsMeta(meta);
						const pdfDebug = meta?.pdf_debug || {};
						hasUnsupportedCompression = Boolean(
							pdfDebug?.has_jpx_decode || pdfDebug?.has_jbig2_decode || pdfDebug?.has_ccitt_decode,
						);
					} catch (commentsError: any) {
						const message = String(commentsError?.response?.data?.message || commentsError?.message || '');
						if (commentsError?.response?.status === 403 || /Brak aktywnego dostępu/i.test(message)) {
							setPages([]);
							setComments([]);
							setError('');
							onClose();
							if (onAccessError) {
								onAccessError(message || 'Brak aktywnego dostępu.');
							}
							return;
						}
						rawItems = [];
					}
				}

				const mappedComments: CommentItem[] = rawItems.map((item: any, idx: number) => ({
					no: Number(item?.no || idx + 1),
					page: Number.isFinite(Number(item?.page)) && Number(item.page) > 0 ? Number(item.page) : null,
					quoted: String(item?.quoted || ''),
					content: String(item?.content || ''),
					type: String(item?.type || 'comment'),
					rect: Array.isArray(item?.rect)
						? item.rect.map((x: any) => Number(x)).filter((x: number) => Number.isFinite(x))
						: [],
					quadpoints: Array.isArray(item?.quadpoints)
						? item.quadpoints.map((x: any) => Number(x)).filter((x: number) => Number.isFinite(x))
						: [],
				}));
				rawCommentsRef.current = mappedComments;

				if (compatMode) {
					return;
				}

				const ver = (pdfjsLib as any).version;
				const loadingTask = (pdfjsLib as any).getDocument({
					url,
					withCredentials: true,
					httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
					cMapUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${ver}/cmaps/`,
					cMapPacked: true,
					standardFontDataUrl: `https://cdn.jsdelivr.net/npm/pdfjs-dist@${ver}/standard_fonts/`,
					isEvalSupported: true,
					disableAutoFetch: false,
					disableStream: false,
				});
				const pdf = await loadingTask.promise;
				const renderedPages: RenderedPage[] = [];
				let blankPages = 0;
				const rotMap = pageRotations || {};
				for (let i = 1; i <= pdf.numPages; i++) {
					const page = await pdf.getPage(i);
					const rotation = totalViewportRotation(page.rotate || 0, rotMap[i]);
					const viewport = page.getViewport({ scale: PDF_VIEW_SCALE, rotation });
					const canvas = document.createElement('canvas');
					canvas.width = viewport.width;
					canvas.height = viewport.height;
					const context = canvas.getContext('2d');
					if (!context) {
						continue;
					}
					await page.render({
						canvasContext: context,
						viewport,
						annotationMode: getPdfAnnotationModeForRender(!!hidePdfAnnotationRender),
					}).promise;
					if (isLikelyBlankCanvas(canvas)) {
						blankPages += 1;
					}
					drawWatermark(canvas, safeWatermark, 0);
					renderedPages.push({
						pageNo: i,
						width: viewport.width,
						height: viewport.height,
						imageSrc: canvas.toDataURL('image/png'),
						pdfRotation: page.rotate || 0,
					});
				}
				const enrichedComments = await enrichCommentsWithPdfViewport(pdf, mappedComments, rotMap);
				if (!cancelled) {
					setPages(renderedPages);
					setComments(enrichedComments);
					const blankFallback = renderedPages.length > 0 && blankPages >= renderedPages.length;
					setCompatFallbackAvailable(blankFallback && hasUnsupportedCompression);
				}
			} catch (e: any) {
				if (!cancelled) {
					const message = String(e?.response?.data?.message || e?.message || 'Nie udało się załadować dokumentu.');
					setPages([]);
					setComments([]);
					if (
						e?.response?.status === 403 ||
						/Brak aktywnego dostępu/i.test(message) ||
						/server response \(403\)/i.test(message)
					) {
						setError('');
						onClose();
						if (onAccessError) {
							onAccessError('Brak aktywnego dostępu.');
						}
					} else {
						setError(message);
					}
				}
			} finally {
				if (!cancelled && !compatMode) {
					setLoading(false);
				}
			}
		};

		load();
		return () => {
			cancelled = true;
		};
	}, [open, url, safeWatermark, token, documentId, compatMode, pageRotations, hidePdfAnnotationRender]);

	const getOverlayBoxes = (pageItem: RenderedPage): OverlayBox[] => {
		if (hideOverlayHighlightBoxes) {
			return [];
		}
		const pageComments = withPosition.filter((item) => item.page === pageItem.pageNo);
		const scale = PDF_VIEW_SCALE;
		const isRotated180 = pageItem.pdfRotation === 180;
		const boxes: OverlayBox[] = [];
		const pw = pageItem.width > 0 ? pageItem.width : 1;
		const ph = pageItem.height > 0 ? pageItem.height : 1;
		const oxPx = commentOffsetXByPage?.[pageItem.pageNo] ?? commentOffsetXGlobal ?? 0;
		const oyPx = commentOffsetYByPage?.[pageItem.pageNo] ?? commentOffsetYGlobal ?? 0;

		const applyOx = (list: OverlayBox[]): OverlayBox[] => {
			if (!oxPx) {
				return list;
			}
			const d = (oxPx / pw) * 100;
			return list.map((b) => ({ ...b, left: b.left + d }));
		};

		const applyOy = (list: OverlayBox[]): OverlayBox[] => {
			if (!oyPx) {
				return list;
			}
			const d = (oyPx / ph) * 100;
			return list.map((b) => ({ ...b, top: b.top + d }));
		};

		const toBox = (xMin: number, xMax: number, yMin: number, yMax: number, key: string, no: number) => {
			const w = (((xMax - xMin) * scale) / pageItem.width) * 100;
			const h = (((yMax - yMin) * scale) / pageItem.height) * 100;
			let left: number, top: number;
			if (isRotated180) {
				left = ((pageItem.width - xMax * scale) / pageItem.width) * 100;
				top = ((yMin * scale) / pageItem.height) * 100;
			} else {
				left = ((xMin * scale) / pageItem.width) * 100;
				top = ((pageItem.height - yMax * scale) / pageItem.height) * 100;
			}
			boxes.push({ key, no, left, top, width: w, height: h });
		};

		pageComments.forEach((item) => {
			if (item.cachedBoxes && item.cachedBoxes.length > 0) {
				boxes.push(...item.cachedBoxes);
				return;
			}
			const pointLikeComment = isPointLikeCommentType(item.type);
			if (!pointLikeComment && item.quadpoints.length >= 8) {
				for (let i = 0; i + 7 < item.quadpoints.length; i += 8) {
					const quad = item.quadpoints.slice(i, i + 8);
					const xs = [quad[0], quad[2], quad[4], quad[6]];
					const ys = [quad[1], quad[3], quad[5], quad[7]];
					toBox(Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys), `${item.no}-q-${i}`, item.no);
				}
			} else if (item.rect.length >= 4) {
				const xMin = Math.min(item.rect[0], item.rect[2]);
				const xMax = Math.max(item.rect[0], item.rect[2]);
				const yMin = Math.min(item.rect[1], item.rect[3]);
				const yMax = Math.max(item.rect[1], item.rect[3]);
				if (pointLikeComment) {
					// Sticky-note style annotations (text/popup) often encode only icon anchors;
					// render a small marker box instead of a full "highlight area".
					const centerX = (xMin + xMax) / 2;
					const centerY = (yMin + yMax) / 2;
					const halfW = 7;
					const halfH = 7;
					toBox(centerX - halfW, centerX + halfW, centerY - halfH, centerY + halfH, `${item.no}-m`, item.no);
				} else {
					toBox(xMin, xMax, yMin, yMax, `${item.no}-r`, item.no);
				}
			}
		});
		const sx = commentScaleXPercent ?? 100;
		const sy = commentScaleYPercent ?? 100;
		let list = scaleOverlayBoxesFromCenter(boxes, sx, sy, pw, ph);
		list = applyOx(list);
		list = applyOy(list);
		const cr = commentRotationDegByPage?.[pageItem.pageNo] ?? 0;
		if (cr) {
			list = rotateOverlayBoxesAroundPageCenter(list, cr, pageItem.width, pageItem.height);
		}
		return list;
	};

	const focusComment = (item: CommentItem) => {
		setActiveNo(item.no);
		if (item.page && pageRefs.current[item.page]) {
			pageRefs.current[item.page]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
		window.setTimeout(() => {
			markerRefs.current[item.no]?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
		}, 180);
		window.setTimeout(() => setActiveNo((prev) => (prev === item.no ? null : prev)), 1800);
	};

	const blockKeyboardActions = (e: React.KeyboardEvent) => {
		const key = e.key.toLowerCase();
		if ((e.ctrlKey || e.metaKey) && ['c', 'p', 's'].includes(key)) {
			e.preventDefault();
		}
	};

	const isDuplicateQuoted = (item: CommentItem) => {
		const normalize = (value: string) =>
			String(value || '')
				.trim()
				.replace(/\s+/g, ' ')
				.toLowerCase();
		const quoted = normalize(item.quoted);
		const content = normalize(item.content);
		return quoted !== '' && quoted === content;
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='xl'
			fullWidth
		>
			<DialogTitle sx={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', pr: 6 }}>
				{title || 'Podgląd pracy'}
				{showRotationInfo && pageRotations && Object.keys(pageRotations).length > 0 ? (
					<Typography
						variant='caption'
						sx={{ display: 'block', color: '#777', mt: 0.5 }}
					>
						Dodatkowy obrót widoku (strona →°):{' '}
						{Object.keys(pageRotations)
							.sort((a, b) => Number(a) - Number(b))
							.map((k) => `${k}→${pageRotations[Number(k)]}°`)
							.join(', ')}
					</Typography>
				) : null}
				{showRotationInfo &&
				(commentScaleXPercent != null || commentScaleYPercent != null) &&
				((commentScaleXPercent ?? 100) !== 100 || (commentScaleYPercent ?? 100) !== 100) ? (
					<Typography
						variant='caption'
						sx={{ display: 'block', color: '#666', mt: 0.25 }}
					>
						Skala nakładki komentarzy (X%, Y% od środka): {commentScaleXPercent ?? 100}% × {commentScaleYPercent ?? 100}
						%
					</Typography>
				) : null}
				{showRotationInfo && hideOverlayHighlightBoxes ? (
					<Typography
						variant='caption'
						sx={{ display: 'block', color: '#a66', mt: 0.25 }}
					>
						Żółte prostokąty (nakładka HTML): wyłączone
					</Typography>
				) : null}
				{showRotationInfo && hidePdfAnnotationRender ? (
					<Typography
						variant='caption'
						sx={{ display: 'block', color: '#a66', mt: 0.25 }}
					>
						Adnotacje PDF na obrazie strony: wyłączone (pdf.js)
					</Typography>
				) : null}
				{showRotationInfo && commentRotationDegByPage && Object.keys(commentRotationDegByPage).length > 0 ? (
					<Typography
						variant='caption'
						sx={{ display: 'block', color: '#666', mt: 0.25 }}
					>
						Obrót tylko komentarzy (strona →°):{' '}
						{Object.keys(commentRotationDegByPage)
							.sort((a, b) => Number(a) - Number(b))
							.map((k) => `${k}→${commentRotationDegByPage[Number(k)]}°`)
							.join(', ')}
					</Typography>
				) : null}
				{adminPreviewMeta ? (
					<Box
						sx={{
							mt: 1,
							p: 1,
							borderRadius: 1,
							bgcolor: 'action.hover',
							border: '1px solid',
							borderColor: 'divider',
						}}
					>
						<Stack
							direction='row'
							flexWrap='wrap'
							alignItems='baseline'
							columnGap={1.5}
							rowGap={0.5}
						>
							<Typography
								component='span'
								sx={{ fontWeight: 700, fontSize: '1.05rem' }}
							>
								Weryfikacja (admin)
							</Typography>
							<Typography
								component='span'
								sx={{
									fontWeight: 700,
									fontSize: '1.35rem',
									lineHeight: 1.2,
									letterSpacing: 0.03,
								}}
							>
								{adminPreviewMeta.work_code || '—'}
							</Typography>
							<Typography
								component='span'
								sx={{ fontSize: '1.0625rem', lineHeight: 1.45 }}
							>
								· Uczeń: {adminPreviewMeta.student_name || '—'} · IIIA:{' '}
								{adminPreviewMeta.third_a_stage_points != null && adminPreviewMeta.third_a_stage_points !== ''
									? String(adminPreviewMeta.third_a_stage_points)
									: '—'}{' '}
								· J1:{' '}
								{adminPreviewMeta.third_a_juror1_points != null && adminPreviewMeta.third_a_juror1_points !== ''
									? String(adminPreviewMeta.third_a_juror1_points)
									: '—'}{' '}
								· J2:{' '}
								{adminPreviewMeta.third_a_juror2_points != null && adminPreviewMeta.third_a_juror2_points !== ''
									? String(adminPreviewMeta.third_a_juror2_points)
									: '—'}
							</Typography>
						</Stack>
						{(() => {
							const dt = String(adminPreviewMeta.document_type || '');
							const shared = Boolean(adminPreviewMeta.shared_review);
							const j1 = String(adminPreviewMeta.assigned_juror1_name || '').trim();
							const j2 = String(adminPreviewMeta.assigned_juror2_name || '').trim();
							const pair = String(adminPreviewMeta.assigned_jury_pair_name || '').trim();
							const hasTeam = pair || j1 || j2;
							if (!hasTeam) {
								return null;
							}
							if (dt === 'review_1' || dt === 'review_2') {
								if (shared) {
									return (
										<Typography
											variant='body1'
											component='div'
											sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
										>
											Recenzja wspólna — jurorzy: {j1 || '—'}, {j2 || '—'}
											{pair ? ` · Para: ${pair}` : ''}
										</Typography>
									);
								}
								if (dt === 'review_1') {
									return (
										<Typography
											variant='body1'
											component='div'
											sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
										>
											Recenzja jurora 1 — juror: {j1 || '—'}
											{pair ? ` · Para: ${pair}` : ''}
										</Typography>
									);
								}
								return (
									<Typography
										variant='body1'
										component='div'
										sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
									>
										Recenzja jurora 2 — juror: {j2 || '—'}
										{pair ? ` · Para: ${pair}` : ''}
									</Typography>
								);
							}
							return (
								<Typography
									variant='body1'
									component='div'
									sx={{ mt: 0.75, fontSize: '1.0625rem', lineHeight: 1.45 }}
								>
									{pair ? `Para: ${pair}` : ''}
									{pair && (j1 || j2) ? ' · ' : ''}
									{j1 || j2 ? `Jurorzy w parze: ${[j1, j2].filter(Boolean).join(', ')}` : ''}
								</Typography>
							);
						})()}
					</Box>
				) : null}
				<IconButton
					onClick={onClose}
					sx={{ position: 'absolute', right: 8, top: 8 }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent
				onKeyDown={blockKeyboardActions}
				onCopy={(e) => e.preventDefault()}
				onCut={(e) => e.preventDefault()}
				onContextMenu={(e) => e.preventDefault()}
				sx={{ overflow: 'hidden', pb: 1 }}
			>
				{loading && (
					<Stack
						direction='row'
						alignItems='center'
						gap={1}
					>
						<CircularProgress size={18} />
						<Typography>Trwa przygotowanie podglądu...</Typography>
					</Stack>
				)}
				{compatFallbackAvailable && !compatMode && (
					<Alert
						severity='warning'
						sx={{ mt: 1, mb: 1 }}
					>
						Wykryto strony wyglądające na puste — PDF zawiera format obrazów (np. JPEG 2000), którego przeglądarka nie
						obsługuje w trybie renderowania z watermarkiem.
						{commentsFetcher && documentId && (
							<Button
								size='small'
								variant='outlined'
								sx={{ ml: 1 }}
								onClick={() => setCompatMode(true)}
							>
								Przekoduj PDF (GhostScript / Cloud Run)
							</Button>
						)}
					</Alert>
				)}
				{!!error && <Typography color='error'>{error}</Typography>}
				<Stack
					direction='row'
					gap={2}
					sx={{ height: '72vh', minHeight: 0 }}
				>
					<Box
						onContextMenu={(e) => e.preventDefault()}
						sx={{
							userSelect: 'none',
							WebkitUserSelect: 'none',
							flex: 1,
							overflow: 'auto',
							pr: 1,
							minHeight: 0,
						}}
					>
						{pages.map((page) => {
							const boxes = getOverlayBoxes(page);
							return (
								<Box
									key={`${page.pageNo}-${page.imageSrc}`}
									ref={(node) => {
										pageRefs.current[page.pageNo] = node as HTMLDivElement | null;
									}}
									sx={{ mb: 2, border: '1px solid #ddd', width: '100%', mx: 'auto' }}
								>
									<Box
										sx={{
											position: 'relative',
											width: '100%',
										}}
									>
										<img
											src={page.imageSrc}
											alt={`Strona ${page.pageNo}`}
											draggable={false}
											style={{ width: '100%', display: 'block', userSelect: 'none' }}
										/>
										{boxes.map((box) => (
											<Box
												key={box.key}
												ref={(node) => {
													if (!markerRefs.current[box.no]) {
														markerRefs.current[box.no] = node as HTMLDivElement | null;
													}
												}}
												onMouseEnter={() => setActiveNo(box.no)}
												onMouseLeave={() => setActiveNo((prev) => (prev === box.no ? null : prev))}
												sx={{
													position: 'absolute',
													left: `${box.left}%`,
													top: `${box.top}%`,
													width: `${box.width}%`,
													height: `${box.height}%`,
													backgroundColor: activeNo === box.no ? 'rgba(255, 111, 0, 0.25)' : 'rgba(255, 235, 59, 0.28)',
													border: activeNo === box.no ? '2px solid #ff6f00' : '1px solid #f9a825',
													pointerEvents: 'auto',
												}}
											/>
										))}
										{boxes.map((box, idx) => (
											<Box
												key={`${box.key}-marker`}
												sx={{
													position: 'absolute',
													left: `${box.left}%`,
													top: `calc(${Math.max(0, box.top)}% - 18px)`,
													background: activeNo === box.no ? '#ef6c00' : '#3949ab',
													color: '#fff',
													fontSize: 11,
													lineHeight: 1,
													px: 0.6,
													py: 0.3,
													borderRadius: 1,
													display: idx === 0 ? 'block' : 'none',
													pointerEvents: 'none',
												}}
											>
												#{box.no}
											</Box>
										))}
									</Box>
								</Box>
							);
						})}
					</Box>
					{hasAnyComments && (
						<Box
							sx={{
								width: 360,
								borderLeft: '1px solid #e0e0e0',
								pl: 1,
								overflow: 'auto',
								minHeight: 0,
								userSelect: 'none',
							}}
						>
							<Typography
								variant='subtitle2'
								sx={{ mb: 1 }}
							>
								Komentarze ({comments.length})
							</Typography>
							{commentsMeta?.source ? (
								<Typography
									variant='caption'
									sx={{ display: 'block', color: '#666', mb: 1 }}
								>
									Źródło: {String(commentsMeta.source)}
								</Typography>
							) : null}
							{withPosition.length === 0 && withoutPosition.length === 0 ? (
								<Typography variant='body2'>Brak komentarzy do wyświetlenia.</Typography>
							) : (
								<>
									{withPosition.length > 0 && (
										<Stack
											spacing={0.8}
											sx={{ mb: 1.5 }}
										>
											{withPosition.map((item) => (
												<Box
													key={`c-pos-${item.no}`}
													onClick={() => focusComment(item)}
													onMouseEnter={() => setActiveNo(item.no)}
													onMouseLeave={() => setActiveNo((prev) => (prev === item.no ? null : prev))}
													sx={{
														border: '1px solid #ddd',
														borderRadius: 1,
														p: 0.8,
														cursor: 'pointer',
														backgroundColor: activeNo === item.no ? 'rgba(255, 152, 0, 0.14)' : '#fff',
													}}
												>
													<Stack
														direction='row'
														justifyContent='space-between'
														alignItems='center'
														mb={0.5}
													>
														<Chip
															size='small'
															label={`#${item.no}`}
														/>
														<Typography variant='caption'>str. {item.page}</Typography>
													</Stack>
													{item.quoted && !isDuplicateQuoted(item) ? (
														<Typography
															variant='caption'
															sx={{ display: 'block', color: '#555' }}
														>
															{item.quoted}
														</Typography>
													) : null}
													<Typography variant='body2'>{item.content || '(brak treści)'}</Typography>
												</Box>
											))}
										</Stack>
									)}
									{withoutPosition.length > 0 && (
										<>
											<Divider sx={{ my: 1 }} />
											<Typography
												variant='caption'
												sx={{ color: '#666' }}
											>
												Komentarze bez pozycji (brak koordynatów)
											</Typography>
											<Stack
												spacing={0.8}
												sx={{ mt: 0.8 }}
											>
												{withoutPosition.map((item) => (
													<Box
														key={`c-nopos-${item.no}`}
														sx={{ border: '1px dashed #cfcfcf', borderRadius: 1, p: 0.8 }}
													>
														<Chip
															size='small'
															label={`#${item.no}`}
															sx={{ mb: 0.5 }}
														/>
														<Typography variant='body2'>{item.content || '(brak treści)'}</Typography>
													</Box>
												))}
											</Stack>
										</>
									)}
								</>
							)}
						</Box>
					)}
				</Stack>
			</DialogContent>
		</Dialog>
	);
};

export default StudentWorkViewer;
